import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import {
  TrendingUp, ArrowDownLeft, ArrowUpRight, Building2,
  CalendarClock, CircleAlert, CircleCheck,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";

const tl = (n) => `₺${new Intl.NumberFormat("tr-TR").format(Math.abs(n))}`;

const CASHFLOW = [
  { ay: "Şub", gelir: 820, gider: 540 },
  { ay: "Mar", gelir: 940, gider: 610 },
  { ay: "Nis", gelir: 880, gider: 560 },
  { ay: "May", gelir: 1120, gider: 690 },
  { ay: "Haz", gelir: 1050, gider: 720 },
  { ay: "Tem", gelir: 1290, gider: 760 },
];

const BARS = [
  { ay: "Şub", net: 280 }, { ay: "Mar", net: 330 }, { ay: "Nis", net: 320 },
  { ay: "May", net: 430 }, { ay: "Haz", net: 330 }, { ay: "Tem", net: 530 },
];

const BANKS = [
  { bank: "Garanti BBVA", hesap: "Vadesiz TL •• 1001", bakiye: 1850000, degisim: "+4,2%" },
  { bank: "Türkiye İş Bankası", hesap: "Vadesiz TL •• 4477", bakiye: 1240000, degisim: "+1,8%" },
  { bank: "Akbank", hesap: "Döviz (USD) •• 9023", bakiye: 512000, degisim: "+6,5%" },
  { bank: "Yapı Kredi", hesap: "POS Bloke •• 3355", bakiye: 248000, degisim: "-0,9%" },
];

const UPCOMING = [
  { ad: "Tedarikçi ödemesi — Delta Lojistik", tutar: -125000, tarih: "14 Tem", tip: "odeme", durum: "Onay bekliyor" },
  { ad: "Fatura tahsilatı — Nova Yapı A.Ş.", tutar: 240000, tarih: "16 Tem", tip: "tahsilat", durum: "Planlandı" },
  { ad: "Kira ödemesi — Merkez Ofis", tutar: -85000, tarih: "20 Tem", tip: "odeme", durum: "Otomatik" },
  { ad: "Fatura tahsilatı — Ege Gıda Ltd.", tutar: 156500, tarih: "22 Tem", tip: "tahsilat", durum: "Planlandı" },
  { ad: "SGK prim ödemesi", tutar: -97400, tarih: "26 Tem", tip: "odeme", durum: "Otomatik" },
];

const TRANSACTIONS = [
  { ad: "POS tahsilatı — Garanti", tutar: 42300, saat: "14:32" },
  { ad: "Havale — Aras Tekstil", tutar: 118000, saat: "11:05" },
  { ad: "Kart ödemesi — AWS EMEA", tutar: -9640, saat: "09:48" },
  { ad: "EFT — Mavi Ofis Mobilya", tutar: -38200, saat: "Dün" },
];

const TABS = [
  { id: "cashflow", label: "Nakit Akışı", testid: "tab-cashflow" },
  { id: "income", label: "Gelir & Gider", testid: "tab-income-expense" },
  { id: "banks", label: "Banka Hesapları", testid: "tab-banks" },
  { id: "upcoming", label: "Yaklaşan Ödemeler", testid: "tab-upcoming" },
];

const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-panel rounded-xl border border-white/10 px-4 py-3 text-xs">
      <div className="mb-1.5 font-semibold text-slate-200">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-slate-400">
          <span className="h-2 w-2 rounded-full" style={{ background: p.stroke || p.fill }} />
          {p.name}: <span className="font-mono-num text-slate-100">{tl(p.value * 1000)}</span>
        </div>
      ))}
    </div>
  );
};

const CashflowPanel = () => (
  <div data-testid="chart-container" className="h-[320px] w-full">
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={CASHFLOW} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
        <defs>
          <linearGradient id="gGelir" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00FF87" stopOpacity={0.45} />
            <stop offset="100%" stopColor="#00FF87" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="gGider" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#EF4444" stopOpacity={0.3} />
            <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
        <XAxis dataKey="ay" stroke="#475569" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
        <YAxis stroke="#475569" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}K`} />
        <Tooltip content={<DarkTooltip />} cursor={{ stroke: "rgba(16,185,129,0.3)" }} />
        <Area type="monotone" dataKey="gelir" name="Gelir" stroke="#00FF87" strokeWidth={2.5} fill="url(#gGelir)" />
        <Area type="monotone" dataKey="gider" name="Gider" stroke="#EF4444" strokeWidth={2} fill="url(#gGider)" />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

const IncomePanel = () => (
  <div className="grid gap-6 lg:grid-cols-5">
    <div className="grid gap-4 lg:col-span-2">
      {[
        { label: "Toplam Gelir (Tem)", value: 1290000, icon: ArrowUpRight, cls: "text-emerald-400" },
        { label: "Toplam Gider (Tem)", value: 760000, icon: ArrowDownLeft, cls: "text-red-400" },
        { label: "Net Nakit Akışı", value: 530000, icon: TrendingUp, cls: "text-emerald-300" },
      ].map((c) => (
        <div key={c.label} className="rounded-2xl border border-white/8 bg-[#0D111A] p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-slate-500">
            <c.icon className={`h-3.5 w-3.5 ${c.cls}`} />
            {c.label}
          </div>
          <div className={`font-mono-num mt-2 text-2xl font-semibold ${c.cls}`}>{tl(c.value)}</div>
        </div>
      ))}
    </div>
    <div className="h-[280px] lg:col-span-3">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={BARS} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
          <XAxis dataKey="ay" stroke="#475569" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
          <YAxis stroke="#475569" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}K`} />
          <Tooltip content={<DarkTooltip />} cursor={{ fill: "rgba(16,185,129,0.06)" }} />
          <Bar dataKey="net" name="Net Akış" fill="#10B981" radius={[6, 6, 0, 0]} maxBarSize={42} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const BanksPanel = () => (
  <div className="grid gap-4 sm:grid-cols-2">
    {BANKS.map((b) => (
      <div
        key={b.bank}
        className="group rounded-2xl border border-white/8 bg-[#0D111A] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-emerald-400/40"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10">
              <Building2 className="h-5 w-5 text-emerald-400" />
            </span>
            <div>
              <div className="text-sm font-semibold text-slate-100">{b.bank}</div>
              <div className="font-mono-num text-xs text-slate-500">{b.hesap}</div>
            </div>
          </div>
          <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400">
            <span className="animate-pulse-dot h-1.5 w-1.5 rounded-full bg-emerald-400" />
            Bağlı
          </span>
        </div>
        <div className="mt-4 flex items-end justify-between">
          <div className="font-mono-num text-xl font-semibold text-slate-50">{tl(b.bakiye)}</div>
          <span className={`text-xs font-medium ${b.degisim.startsWith("-") ? "text-red-400" : "text-emerald-400"}`}>
            {b.degisim} / 30g
          </span>
        </div>
      </div>
    ))}
  </div>
);

const UpcomingPanel = () => (
  <div className="divide-y divide-white/5">
    {UPCOMING.map((u) => (
      <div key={u.ad} className="flex items-center justify-between gap-4 py-4">
        <div className="flex items-center gap-3.5">
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${u.tip === "tahsilat" ? "bg-emerald-500/10" : "bg-amber-500/10"}`}>
            {u.tip === "tahsilat"
              ? <CircleCheck className="h-5 w-5 text-emerald-400" />
              : <CircleAlert className="h-5 w-5 text-amber-400" />}
          </span>
          <div>
            <div className="text-sm font-medium text-slate-100">{u.ad}</div>
            <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
              <CalendarClock className="h-3 w-3" />
              {u.tarih} · {u.durum}
            </div>
          </div>
        </div>
        <div className={`font-mono-num shrink-0 text-sm font-semibold ${u.tutar > 0 ? "text-emerald-400" : "text-red-400"}`}>
          {u.tutar > 0 ? "+" : "−"}{tl(u.tutar)}
        </div>
      </div>
    ))}
  </div>
);

const PANELS = { cashflow: CashflowPanel, income: IncomePanel, banks: BanksPanel, upcoming: UpcomingPanel };

const DashboardPreview = () => {
  const [tab, setTab] = useState("cashflow");
  const Panel = PANELS[tab];

  return (
    <section id="demo" data-testid="dashboard-preview" className="relative py-24 sm:py-32">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-emerald-500/8 blur-[160px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <Reveal className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">
            Canlı Önizleme
          </div>
          <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
            Panelini şimdiden dene.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg">
            Gerçek üründen alınmış örnek verilerle nakit akışını, hesaplarını ve
            yaklaşan ödemelerini keşfet.
          </p>
        </Reveal>

        <Reveal delay={0.15} className="mt-12">
          <div className="glass-panel overflow-hidden rounded-3xl border border-white/10 shadow-[0_40px_120px_rgba(0,0,0,0.55)]">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/8 px-5 py-4 sm:px-7">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-red-500/70" />
                <span className="h-3 w-3 rounded-full bg-amber-500/70" />
                <span className="h-3 w-3 rounded-full bg-emerald-500/70" />
              </div>
              <div className="flex flex-wrap gap-1.5 rounded-full border border-white/8 bg-[#07090E]/60 p-1">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    data-testid={t.testid}
                    onClick={() => setTab(t.id)}
                    className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-300 sm:text-sm ${
                      tab === t.id
                        ? "bg-emerald-500 text-emerald-950 shadow-[0_0_20px_rgba(0,255,135,0.3)]"
                        : "text-slate-400 hover:text-slate-100"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-5 sm:p-7">
              <AnimatePresence mode="wait">
                <motion.div
                  key={tab}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                >
                  <Panel />
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="border-t border-white/8 px-5 py-5 sm:px-7">
              <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                <span className="animate-pulse-dot h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Son İşlemler
              </div>
              <ul data-testid="recent-transactions-list" className="grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
                {TRANSACTIONS.map((t) => (
                  <li key={t.ad} className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">{t.ad} <span className="text-slate-600">· {t.saat}</span></span>
                    <span className={`font-mono-num font-medium ${t.tutar > 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {t.tutar > 0 ? "+" : "−"}{tl(t.tutar)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/panel"
              data-testid="open-live-panel-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 text-sm font-semibold text-emerald-950 transition-all duration-300 hover:bg-emerald-400 hover:shadow-[0_0_36px_rgba(0,255,135,0.4)]"
            >
              Kendi Panelini Aç
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <p className="mt-3 text-xs text-slate-600">
              Örnek verilerle değil, kendi kayıtlarınla çalışan sürüm — eklediğin her şey kalıcı olarak kaydedilir.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default DashboardPreview;
